import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, Mail, UserCog, ShieldCheck, Loader2, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { api, errMsg } from "@/lib/api";

const CHAIN = [
  ["login", "Super admin", Lock],
  ["otp", "Email OTP", Mail],
  ["user_manager", "User manager", UserCog],
  ["portal", "Portal", ShieldCheck],
];

const ChainBar = ({ step }) => {
  const idx = CHAIN.findIndex((c) => c[0] === step);
  return (
    <ol className="mb-6 grid grid-cols-4 gap-1.5" data-testid="auth-chain-indicator">
      {CHAIN.map(([k, label, Icon], i) => (
        <li key={k} className={`min-w-0 break-words rounded-xl border p-2 text-center text-[11px] font-semibold leading-tight ${i < idx ? "border-[#BADBCE] bg-[#D1E7DD] text-[#0F5132]" : i === idx ? "border-[#B8901F] bg-[#FAF4E1] text-[#7a5f12]" : "border-border text-muted-foreground"}`}>
          {i < idx ? <Check size={14} className="mx-auto mb-1" /> : <Icon size={14} className="mx-auto mb-1" />}
          {label}
          <span className="sr-only">{i < idx ? "done" : i === idx ? "current" : "pending"}</span>
        </li>
      ))}
    </ol>
  );
};

export default function AdminLogin() {
  const nav = useNavigate();
  const [step, setStep] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/auth/state").then(({ data }) => (data.next === "portal" ? nav("/portal") : setStep(data.next)));
  }, [nav]);

  const run = async (fn) => {
    setBusy(true); setErr("");
    try { await fn(); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  const login = (e) => { e.preventDefault(); run(async () => { const { data } = await api.post("/admin/auth/login", { email, password }); setInfo(data); setStep("otp"); }); };
  const verify = (e) => { e.preventDefault(); run(async () => { await api.post("/admin/auth/verify-otp", { code }); setStep("user_manager"); }); };
  const resend = () => run(async () => { const { data } = await api.post("/admin/auth/resend-otp"); setInfo((x) => ({ ...x, ...data })); setCode(""); toast.success("New code sent – previous code invalidated"); });
  const authorize = () => run(async () => { await api.post("/admin/auth/authorize-manager"); nav("/portal"); });
  const restart = () => api.post("/admin/auth/logout").then(() => { setStep("login"); setInfo(null); setCode(""); });

  const field = "h-12 w-full rounded-xl border border-input bg-card px-4 text-sm";
  return (
    <div className="q1-pattern flex min-h-screen items-center justify-center p-4" data-testid="admin-login-page">
      <div className="w-full max-w-md rounded-3xl bg-background p-6 shadow-2xl sm:p-8">
        <Link to="/" className="mb-4 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground" data-testid="admin-back-home"><ArrowLeft size={14} /> Qatar One app</Link>
        <h1 className="text-2xl font-bold">Administrator portal</h1>
        <p className="mb-5 mt-1 text-xs text-muted-foreground">Strict chain: password → email OTP → user-manager authorization → portal. Enforced on the server.</p>
        {step && <ChainBar step={step} />}
        {!step && <Loader2 className="mx-auto animate-spin" />}
        {err && <p className="mb-3 rounded-lg border border-[#F5C2C7] bg-[#F8D7DA] p-2.5 text-xs font-semibold text-[#842029]" role="alert" data-testid="admin-auth-error">✕ {err}</p>}
        {step === "login" && (
          <form onSubmit={login} className="space-y-3">
            <input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@…" className={field} autoComplete="username" />
            <input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={field} autoComplete="current-password" />
            <button data-testid="admin-login-submit-btn" disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary font-semibold text-primary-foreground disabled:opacity-60">{busy && <Loader2 size={16} className="animate-spin" />} Continue</button>
            <p className="text-[11px] text-muted-foreground">Email + password alone never grants portal access.</p>
          </form>
        )}
        {step === "otp" && (
          <form onSubmit={verify} className="space-y-4">
            <p className="text-sm">A 6-digit code was {info?.email_sent === false ? "generated (email delivery failed – check logs)" : "emailed"} {info?.masked_email ? `to ${info.masked_email}` : "to the administrator address"}. It expires in {Math.round((info?.otp_ttl_seconds || 300) / 60)} minutes and works once.</p>
            {info?.sandbox_otp && <p className="rounded-lg bg-[#FAF4E1] p-2.5 text-xs text-[#664D03]" data-testid="admin-sandbox-otp">Sandbox-only display: <b className="tracking-widest">{info.sandbox_otp}</b> (disable in Governance settings)</p>}
            <div className="flex justify-center" dir="ltr">
              <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="admin-otp-input">
                <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="h-12 w-11" />)}</InputOTPGroup>
              </InputOTP>
            </div>
            <button data-testid="admin-otp-verify-btn" disabled={busy || code.length < 6} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary font-semibold text-primary-foreground disabled:opacity-60">{busy && <Loader2 size={16} className="animate-spin" />} Verify code</button>
            <div className="flex justify-between text-xs font-semibold">
              <button type="button" onClick={resend} data-testid="admin-otp-resend-btn" className="text-primary">Send a new code</button>
              <button type="button" onClick={restart} data-testid="admin-restart-btn" className="text-muted-foreground">Start over</button>
            </div>
          </form>
        )}
        {step === "user_manager" && (
          <div className="space-y-4">
            <p className="text-sm">MFA verified. Final step: authorize the <b>PORTAL_USER_MANAGER</b> context for this session. Access is granted only when all four server-side states are true.</p>
            <button data-testid="admin-authorize-manager-btn" onClick={authorize} disabled={busy} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#B8901F] font-semibold text-white disabled:opacity-60">{busy && <Loader2 size={16} className="animate-spin" />} Authorize & enter portal</button>
          </div>
        )}
      </div>
    </div>
  );
}
